import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Pencil, Plus, Trash2 } from 'lucide-react';

import { useListSprints } from '@data';

import { Button } from '@ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components/ui/dropdown-menu';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

import ConfirmDialog from '../../ConfirmDialog';
import SprintRangePicker from './SprintRangePicker';
import {
  absorberOf,
  type MoveProblem,
  type SplitProblem,
  dayBefore,
  formatDay,
  formatShortDay,
  moveProblem,
  opensYear,
  moveStart,
  nameOf,
  nameTaken,
  nextNumber,
  ProductVersion,
  removeProblem,
  splitAt,
  splitProblem,
  versionCovering,
  yearOf,
} from './productVersions';
import { sprintOfDay, type SowSprint } from './statementsOfWork';
import { useUnsavedWork } from './unsavedWork';
import { useProductViewer } from './productViewer';
import type { ProductVersions } from './useProductVersions';

/** One date, not two: a year is covered end to end, so there is no overlap to check for. */
const VersionPicker = ({ versions: state }: { versions: ProductVersions }): JSX.Element | null => {
  const { t } = useTranslation();
  const { guard } = useUnsavedWork();
  const { isClient } = useProductViewer();
  const { versions, version, select, create, update, remove } = state;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ProductVersion | null>(null);
  const [removing, setRemoving] = useState<ProductVersion | null>(null);
  const [startDate, setStartDate] = useState('');
  const [name, setName] = useState('');
  // Where somebody is looking, not what they have picked: paging leaves the held sprint alone.
  const [pickerYear, setPickerYear] = useState('');
  // Every sprint up to this year's: which of them a version may start on is decided per dialog.
  const { data: sprintPage } = useListSprints({ limit: 400 });
  const sprints: SowSprint[] = useMemo(
    () =>
      (sprintPage?.items ?? [])
        .filter(sprint => Number(sprint.startDate.slice(0, 4)) <= new Date().getFullYear())
        .map(sprint => ({
          id: sprint.id,
          number: sprint.number,
          startDate: sprint.startDate.slice(0, 10),
          endDate: sprint.endDate.slice(0, 10),
        })),
    [sprintPage],
  );

  if (!version) return null;

  const running = versions[versions.length - 1];
  /** The last of the chain says it holds on, rather than naming a 31 December nobody agreed to. */
  const spanOf = (each: ProductVersion): string =>
    each.number === running.number
      ? t('productVersions.spanOpen', { from: formatShortDay(each.startDate) })
      : t('productVersions.span', { from: formatShortDay(each.startDate), to: formatShortDay(each.endDate) });

  /** From the day, not a stored sprint id: a version saved before the grid can start mid-sprint. */
  const sprintAt = (day: string): string => sprintOfDay(sprints, day)?.id ?? '';
  const dayOfSprint = (id: string): string => sprints.find(sprint => sprint.id === id)?.startDate ?? '';

  /** Which sprints a dialog may pick: the ones inside the span it is allowed to cut. */
  const takenOutside = (from: string, to: string): string[] =>
    sprints.filter(sprint => sprint.startDate <= from || sprint.startDate > to).map(sprint => sprint.id);

  /** Read off the chain the dialog would leave, so a moving end says the end it is about to have. */
  const coveredBands = (
    chain: ProductVersion[],
    except: string,
  ): { key: string; name: string; sprintIds: string[]; open?: boolean }[] =>
    chain
      .filter(each => each.number !== except)
      .map(each => ({
        key: each.number,
        name: nameOf(each),
        // The last of the chain holds until something replaces it, so it is drawn fading out.
        open: each.number === chain[chain.length - 1].number,
        // Sorted here: the list route has no ORDER BY, and the grid reads this order as distance along the band.
        sprintIds: sprints
          .filter(sprint => versionCovering(chain, sprint.startDate)?.number === each.number)
          .sort((one, other) => one.startDate.localeCompare(other.startDate))
          .map(sprint => sprint.id),
      }))
      .filter(band => band.sprintIds.length > 0);

  /** Nothing left to validate, which is what the closing dialog would otherwise do against its own work. */
  const clearDraft = (): void => {
    setStartDate('');
    setName('');
  };

  const openCreate = (): void => {
    setPickerYear(yearOf(running.startDate));
    setStartDate('');
    setName(nextNumber(versions, yearOf(running.startDate)).slice(2));
    setCreating(true);
  };
  const openEdit = (each: ProductVersion): void => {
    setPickerYear(yearOf(each.startDate));
    setStartDate(each.startDate);
    setName(nameOf(each));
    setEditing(each);
  };

  // Worked out from the model, so the sentence shown and the answer given to Save cannot disagree.
  const createProblem = splitProblem(versions, startDate);
  const createReasons: Record<SplitProblem, string> = {
    // Nothing typed yet is not something to complain about: the field's own label is the instruction.
    noDay: '',
    notLater: t('productVersions.problems.notLater', { day: formatDay(running.startDate) }),
    pastEnd: t('productVersions.problems.pastEnd', { day: formatDay(running.endDate) }),
  };
  const createReason = createProblem ? createReasons[createProblem] : '';

  const previousOf = (each: ProductVersion): ProductVersion | undefined =>
    versions[versions.findIndex(one => one.number === each.number) - 1];
  const editProblem = editing ? moveProblem(versions, editing.number, startDate) : null;
  const editReasons: Record<MoveProblem, string> = {
    noDay: '',
    unknown: '',
    // Answered by the hint under the disabled field rather than as a refusal.
    first: '',
    notLater: t('productVersions.problems.afterPrevious', {
      day: formatDay(editing ? (previousOf(editing)?.startDate ?? '') : ''),
    }),
    pastEnd: t('productVersions.problems.beforeOwnEnd', { day: formatDay(editing?.endDate ?? '') }),
    otherYear: t('productVersions.problems.otherYear', { year: yearOf(editing?.startDate ?? '') }),
  };
  const editReason = editProblem ? editReasons[editProblem] : '';
  // Pinned: the chain's first version, and any that opens a year, have no boundary they may move.
  const editPinned = !!editing && (editProblem === 'first' || opensYear(versions, editing.number));
  /** The boundary has been moved somewhere the version before it has to follow. */
  const moved = !!editing && editProblem === null && startDate !== editing.startDate && !!previousOf(editing);

  /** Filled in rather than placeheld, so no sentence is needed explaining what leaving it alone does. */
  const nameField = (number: string, fallback: string): JSX.Element => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="version-name">{t('productVersions.name')}</Label>
      <Input id="version-name" value={name} onChange={event => setName(event.target.value)} placeholder={fallback} />
      {nameTaken(versions, number, name) && (
        <p className="text-xs text-destructive">{t('productVersions.problems.nameTaken', { name: name.trim() })}</p>
      )}
    </div>
  );
  /** The number the new version will get, which is where its default name comes from. */
  const createNumber = nextNumber(versions, yearOf(startDate || running.startDate));
  const createChain = createProblem ? versions : splitAt(versions, startDate).versions;
  /** What a deleted version's span goes to, by name. */
  const absorbedBy = (each: ProductVersion): string => {
    const absorber = absorberOf(versions, each.number);
    return absorber ? nameOf(absorber) : '';
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        {/* The name and nothing else: the span as well made the button the longest thing on the row. */}
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size="sm" className="bg-card shadow-xs">
              {/* Keyed by the version it names, so a switch fades rather than swapping under the pointer. */}
              <span
                key={version.number}
                className="flex animate-in items-center gap-1.5 fade-in duration-[180ms] motion-reduce:animate-none"
              >
                <span className="text-muted-foreground">{t('productVersions.label')}</span>
                <span className="font-semibold">{nameOf(version)}</span>
              </span>
              <ChevronDown className="size-4 text-muted-foreground" />
            </Button>
          }
        />
        {/* Sized to its content: the base menu is as wide as its trigger, which here is a short button. */}
        <DropdownMenuContent align="end" className="w-auto min-w-48 max-w-80">
          {/* Newest first, and the label inside the group it names, which is what base-ui's menu asks for. */}
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t('productVersions.label')}</DropdownMenuLabel>
            {[...versions].reverse().map(each => (
              <DropdownMenuItem
                key={each.number}
                // Switching version drops any typing on the tab, so it asks first, as leaving does.
                onClick={() => guard(() => select(each.number))}
                className="gap-2"
              >
                <Check className={each.number === version.number ? 'size-4' : 'invisible size-4'} />
                <span className="flex flex-col">
                  <span className="font-medium text-foreground">{nameOf(each)}</span>
                  <span className="text-xs text-muted-foreground">{spanOf(each)}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {/* A client picks which version to read; making and changing them is ours. */}
          {!isClient && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openCreate}>
                <Plus className="size-4" />
                {t('productVersions.new')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openEdit(version)}>
                <Pencil className="size-4" />
                {t('productVersions.editItem', { number: nameOf(version) })}
              </DropdownMenuItem>
              {/* A year has to keep a version, so the only one it has cannot be deleted. */}
              {!removeProblem(versions, version.number) && (
                <DropdownMenuItem variant="destructive" onClick={() => setRemoving(version)}>
                  <Trash2 className="size-4" />
                  {t('productVersions.deleteItem', { number: nameOf(version) })}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('productVersions.newTitle')}</DialogTitle>
            <DialogDescription>{t('productVersions.newHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {nameField('', createNumber.slice(2))}
            {/* The sprints the running version covers, less the one it opened on: it has to keep a start. */}
            <SprintRangePicker
              sprints={sprints}
              hint={t('productVersions.pickerHint')}
              single
              openEnded
              leftYear={pickerYear}
              onLeftYear={setPickerYear}
              startId={sprintAt(startDate)}
              endId=""
              taken={takenOutside(running.startDate, running.endDate)}
              covered={coveredBands(createChain, createNumber)}
              onPick={id => setStartDate(dayOfSprint(id))}
            />
            {/* A quiet line for the reader unsure the grid means what they think, skippable by the rest. */}
            <p
              // Keyed by whether it speaks, not by what it says, or every click blinks the line.
              key={createProblem ? 'quiet' : 'said'}
              className="min-h-4 animate-in fade-in text-center text-xs text-muted-foreground duration-[180ms] motion-reduce:animate-none"
            >
              {!createProblem &&
                t('productVersions.covers', {
                  number: name.trim() || createNumber.slice(2),
                  from: formatDay(startDate),
                })}
            </p>
            {createReason && <p className="text-sm text-destructive">{createReason}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              disabled={!!createProblem || nameTaken(versions, '', name)}
              onClick={() =>
                guard(() => {
                  if (!create(startDate, name.trim())) return;
                  setCreating(false);
                  clearDraft();
                })
              }
            >
              {t('productVersions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('productVersions.editTitle', { number: editing ? nameOf(editing) : '' })}</DialogTitle>
            {/* A pinned version has no boundary to move, so the sentence about moving one is not offered. */}
            <DialogDescription>
              {editPinned
                ? t('productVersions.editFirstHint', { number: editing ? nameOf(editing) : '' })
                : t('productVersions.editHint')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {editing && nameField(editing.number, editing.number.slice(2))}
            {/* A pinned version opens on 1 January, so there is no boundary to move and no grid to draw. */}
            {!editPinned && editing && (
              <SprintRangePicker
                sprints={sprints}
                hint={t('productVersions.editPickerHint')}
                single
                openEnded
                leftYear={pickerYear}
                onLeftYear={setPickerYear}
                startId={sprintAt(startDate)}
                endId=""
                // Between the previous version and its own end: what this version will cover.
                taken={takenOutside(previousOf(editing)?.startDate ?? '', editing.endDate)}
                covered={coveredBands(
                  editProblem ? versions : moveStart(versions, editing.number, startDate),
                  editing.number,
                )}
                onPick={id => setStartDate(dayOfSprint(id))}
              />
            )}
            {/* Moving this boundary moves the previous version's end, the half the grid cannot draw. */}
            {!editPinned && (
              <p
                key={moved ? 'said' : 'quiet'}
                className="min-h-4 animate-in fade-in text-center text-xs text-muted-foreground duration-[180ms] motion-reduce:animate-none"
              >
                {moved &&
                  t('productVersions.shortens', {
                    number: nameOf(previousOf(editing as ProductVersion) as ProductVersion),
                    day: formatDay(dayBefore(startDate)),
                  })}
              </p>
            )}
            {editReason && <p className="text-sm text-destructive">{editReason}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t('actions.cancel')}
            </Button>
            <Button
              disabled={(!!editProblem && !editPinned) || nameTaken(versions, editing?.number ?? '', name)}
              onClick={() => {
                if (!editing) return;
                // The first version's date is fixed, so its own start is sent back unchanged.
                const wanted = editPinned ? editing.startDate : startDate;
                if (!update(editing.number, wanted, name.trim())) return;
                setEditing(null);
                clearDraft();
              }}
            >
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={open => !open && setRemoving(null)}
        title={t('productVersions.deleteTitle', { number: removing ? nameOf(removing) : '' })}
        description={
          removing
            ? t('productVersions.deleteHint', {
                number: nameOf(removing),
                neighbour: absorbedBy(removing),
              })
            : undefined
        }
        confirmLabel={t('actions.remove')}
        onConfirm={() => removing && guard(() => remove(removing.number))}
      />
    </div>
  );
};

export default VersionPicker;
